import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  GetApp as ExportIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  IosShare as ShareIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import PedigreeExporter from '../services/pedigreeExporter';
import PedigreeTree from '../components/PedigreeTree';
import { getBirdListLabels } from '../utils/birdSearch';
import { openPdfInNewTab } from '../utils/pdfWindow';
import WebApi from '../services/webApi';
import { IS_WEB } from '../services/platform';
import { useLicense } from '../context/LicenseContext';
import {
  BACKGROUND_KEY,
  BACKGROUND_FADE_KEY,
  backgroundStyles
} from '../utils/pedigreeBackground';

// One tile of the diagonal "SIMPLIFIED PREVIEW" watermark, repeated across
// the preview panel as a CSS background so it covers any panel size.
const WATERMARK_TILE = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="380" height="260">` +
    `<text x="190" y="130" text-anchor="middle" transform="rotate(-30 190 130)" ` +
    `font-family="Arial, Helvetica, sans-serif" font-size="26" font-weight="700" ` +
    `letter-spacing="4" fill="rgba(31,63,120,0.10)">SIMPLIFIED PREVIEW</text>` +
  `</svg>`
);

const PedigreeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { reload: reloadLicense } = useLicense();
  const [bird, setBird] = useState(null);
  const [commonAncestors, setCommonAncestors] = useState({
    allDuplicateIds: [],
    duplicates: new Map()
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pedigreeData, setPedigreeData] = useState(null);
  // Loft background image + overlay strength, mirrored from Settings so the
  // preview panel shows the paper the PDF will actually be printed on.
  const [background, setBackground] = useState(null);
  // Web only: the just-downloaded PDF, shown in a success popup. On touch
  // devices with the Web Share API (iPhone/Android) the popup's action opens
  // the native share sheet — Save to Files, Open in..., AirDrop — because
  // iOS Safari both hides its downloads folder well and silently refuses
  // direct blob navigation. The generated blob also backs the new-tab action.
  // The ref mirrors the state so cleanup paths can revoke the blob URL.
  const [downloadedPdf, setDownloadedPdf] = useState(null); // { url, canShare, isAuto }
  const [pdfOpenError, setPdfOpenError] = useState('');
  const downloadedPdfRef = React.useRef(null); // { url, file, isAuto }

  // The generator decides canShare/downloaded (the auto-download is skipped
  // only on iOS, where Safari opens blob downloads inline instead of saving
  // them); this just holds the result for the popup.
  const showDownloadDialog = (result, isAuto) => {
    downloadedPdfRef.current = { url: result.viewUrl, file: result.file, isAuto };
    setPdfOpenError('');
    setDownloadedPdf({
      url: result.viewUrl,
      canShare: result.canShare,
      downloaded: result.downloaded,
      isAuto
    });
  };

  // Close the popup and release the temporary blob URL. When the popup
  // belonged to an auto-export (Quick Actions on the bird profile), closing it
  // returns the user to where they came from.
  const closeDownloadDialog = () => {
    const current = downloadedPdfRef.current;
    downloadedPdfRef.current = null;
    setDownloadedPdf(null);
    setPdfOpenError('');
    if (!current) return;
    URL.revokeObjectURL(current.url);
    if (current.isAuto) navigate(-1);
  };

  // A dialog left open across bird navigation would offer the PREVIOUS
  // bird's PDF; drop it (and its blob URL) whenever the route id changes or
  // the page unmounts.
  useEffect(() => {
    setDownloadedPdf(null);
    setPdfOpenError('');
    return () => {
      if (downloadedPdfRef.current) {
        URL.revokeObjectURL(downloadedPdfRef.current.url);
        downloadedPdfRef.current = null;
      }
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      loadBirdData();
    }
  }, [id]);

  const autoExportTriggered = React.useRef(false);
  useEffect(() => {
    // Check if auto-export is requested (only trigger once)
    if (searchParams.get('export') === 'pdf' && bird && !exporting && !autoExportTriggered.current) {
      autoExportTriggered.current = true;
      handleExportPDF(true);
    }
  }, [searchParams, bird]);

  const loadBirdData = async () => {
    try {
      const [birdData, ancestorData, pedigree, settings] = await Promise.all([
        DatabaseService.getBirdById(id),
        DatabaseService.findCommonAncestors(id),
        DatabaseService.getPedigree(id, 5),
        // Decoration only — a settings read that fails must not stop the
        // pedigree from loading.
        DatabaseService.getSettings().catch(() => ({}))
      ]);

      setBird(birdData);
      setCommonAncestors({
        allDuplicateIds: ancestorData.allDuplicateIds,
        duplicates: ancestorData.duplicates
      });
      setPedigreeData(pedigree);
      setBackground(
        backgroundStyles(settings[BACKGROUND_KEY], settings[BACKGROUND_FADE_KEY])
      );
    } catch (error) {
      console.error('Error loading bird data:', error);
      setError('Error loading bird data');
    } finally {
      setLoading(false);
    }
  };

  // isAutoExport marks calls made by the ?export=pdf effect above. It is a
  // parameter (not re-read from the URL) so a stale query param — e.g. an
  // auto-export that was refused by the license gate — can't make a later
  // MANUAL download suppress the popup and navigate away mid-read.
  const handleExportPDF = async (isAutoExport = false) => {
    if (!bird) return;

    setExporting(true);
    setError('');
    setSuccess('');
    // Clear any lingering popup from a previous export (without its
    // navigate-back side effect — this is a fresh export, not a dismissal).
    if (downloadedPdfRef.current) {
      URL.revokeObjectURL(downloadedPdfRef.current.url);
      downloadedPdfRef.current = null;
      setDownloadedPdf(null);
      setPdfOpenError('');
    }

    try {
      // License gate: trials get a limited number of PDF exports; read-only
      // installs get none. The main process (or web API) is authoritative —
      // this pre-check just avoids generating a PDF that would be refused.
      let lic = null;
      if (window.electronAPI && window.electronAPI.getLicenseState) {
        lic = await window.electronAPI.getLicenseState();
      } else if (IS_WEB) {
        lic = await WebApi.licenseState();
      }
      if (lic) {
        if (lic.mode === 'readonly' || lic.mode === 'unlicensed') {
          setError('An active license (or free trial) is required to export pedigree PDFs.');
          return;
        }
        if (lic.mode === 'trial' && lic.trial && lic.trial.pdfUsed >= lic.trial.maxPdf) {
          setError(`The free trial includes ${lic.trial.maxPdf} pedigree PDF downloads — you've used them all. Choose a paid plan for unlimited exports.`);
          return;
        }
      }

      // Get the latest duplicate colors and ancestor IDs
      const { allDuplicateIds: ancestorIds, duplicates: duplicateColors } = await DatabaseService.findCommonAncestors(id);

      // Pass both ancestor IDs and duplicate colors to the PDF exporter
      const result = await PedigreeExporter.exportToPDF(bird, ancestorIds, duplicateColors);

      if (result && result.canceled) {
        // User dismissed the save dialog — not an error, but not a success either.
        return;
      }

      // Count the export against the trial allowance (no-op on paid plans).
      if (window.electronAPI && window.electronAPI.recordPdfExport) {
        await window.electronAPI.recordPdfExport();
      } else if (IS_WEB) {
        // A failed recording must not mark the already-downloaded PDF as an
        // error, but the license banner should reflect the new count — the
        // desktop main process broadcasts this automatically; on web we
        // refresh the mirrored state ourselves.
        const exportId = typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Array.from(crypto.getRandomValues(new Uint32Array(2))).join('-')}`;
        try {
          await WebApi.recordPdfExport(exportId, {
            bird_id: Number(bird.id),
            filename: result?.filename || 'Pedigree.pdf'
          });
        } catch (recordError) {
          // Paid export events are idempotent, so one retry improves accounting
          // during a brief network interruption without double-counting.
          if (lic?.mode === 'full' || lic?.mode === 'past_due') {
            await WebApi.recordPdfExport(exportId, {
              bird_id: Number(bird.id),
              filename: result?.filename || 'Pedigree.pdf'
            }).catch(() => {});
          }
        }
        await reloadLicense().catch(() => {});
      }
      // Browser downloads land silently (and on iOS, unreliably), so every
      // web export — manual or Quick Actions auto-export — confirms with the
      // popup, which offers a new browser tab or the native share sheet
      // (touch devices). For auto-exports, dismissing the popup navigates
      // back to where the user came from. The desktop app uses a native
      // save dialog (no viewUrl), so its alert is confirmation enough.
      if (result?.viewUrl) {
        showDownloadDialog(result, isAutoExport);
      } else {
        setSuccess('PDF downloaded successfully!');
        if (isAutoExport) {
          setTimeout(() => {
            navigate(-1);
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Error generating PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading bird data...</Typography>
      </Box>
    );
  }

  if (!bird) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          Bird not found
        </Typography>
        <Button onClick={() => navigate('/birds')} sx={{ mt: 2 }}>
          Back to Birds
        </Button>
      </Box>
    );
  }

  // Name preferred, ring as fallback; ringLabel is null when the ring already
  // is the displayed name (so it isn't shown twice).
  const { displayName, ringLabel } = getBirdListLabels(bird);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Back
        </Button>
      </Box>

      {/* Bird Info */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {displayName}
        </Typography>
        {ringLabel && (
          <Typography variant="body1" color="text.secondary">
            Ring Details: {ringLabel}
          </Typography>
        )}
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Pedigree Preview */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Preview Only:</strong> This is a simplified preview. The downloadable PDF will show complete details, full formatting, and professional layout.
        </Typography>
      </Alert>

      {/* Download Button — the page's main action, kept front and center */}
      <Box display="flex" justifyContent="center" mb={3}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <ExportIcon />}
          onClick={() => handleExportPDF()}
          disabled={exporting}
          sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 48 }}
        >
          {exporting ? 'Generating PDF...' : 'Download Pedigree PDF'}
        </Button>
      </Box>

      {/* The panel carries the loft's pedigree background (already under its
          white overlay) so the preview shows the paper the PDF prints on.
          The tree's own cards stay opaque, exactly as the PDF's photo boxes
          and header strips do. */}
      <Paper
        elevation={2}
        sx={{ p: { xs: 1.5, md: 3 }, position: 'relative', overflow: 'hidden', ...background }}
      >
        {/* Small screens: the 5-generation grid is wider than the phone, so
            it scrolls sideways — tell the user. Hidden on md+ where the grid
            usually fits. */}
        <Typography
          variant="caption"
          sx={{
            display: { xs: 'block', md: 'none' },
            mb: 1,
            color: 'text.secondary',
            fontWeight: 600
          }}
        >
          Swipe sideways to see all 5 generations →
        </Typography>
        <PedigreeTree
          pedigreeData={pedigreeData}
          commonAncestors={commonAncestors.allDuplicateIds}
          duplicateColors={commonAncestors.duplicates}
        />
        {/* Watermark over the preview panel. pointerEvents: 'none' keeps the
            tree fully interactive underneath; the tiled SVG covers the panel
            at any size, including when the tree scrolls sideways under it. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            backgroundImage: `url("data:image/svg+xml,${WATERMARK_TILE}")`
          }}
        />
      </Paper>

      {/* Web only: download-success popup. The PDF opens in the browser's
          native viewer in a new tab; touch devices also get the native share
          sheet (Save to Files / Open in... / AirDrop). */}
      <Dialog
        open={Boolean(downloadedPdf)}
        onClose={() => closeDownloadDialog()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleIcon color="success" />
          {downloadedPdf?.downloaded ? 'PDF downloaded' : 'Pedigree PDF ready'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {!downloadedPdf?.downloaded
              ? 'Your pedigree PDF is ready. Save it to a folder of your choice, open it in another app, or share it straight from here.'
              : downloadedPdf?.canShare
              ? "Your pedigree PDF has been saved to your device's Downloads. You can also open it in a new tab or share it straight from here."
              : "Your pedigree PDF has been saved to your device's downloads. You can also open it in a new tab to check it over."}
          </Typography>
          {pdfOpenError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {pdfOpenError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => closeDownloadDialog()}>Close</Button>
          <Button
            variant={downloadedPdf?.canShare ? 'outlined' : 'contained'}
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              const current = downloadedPdfRef.current;
              if (!current || !openPdfInNewTab(current.url)) {
                setPdfOpenError('Your browser blocked the PDF tab. Allow pop-ups for this site, then try again.');
                return;
              }
              // Delayed URL cleanup is now owned by the new-tab helper, so
              // route cleanup cannot revoke the PDF while it is still loading.
              downloadedPdfRef.current = null;
              setDownloadedPdf(null);
              setPdfOpenError('');
              if (current.isAuto) navigate(-1);
            }}
          >
            Open in new tab
          </Button>
          {downloadedPdf?.canShare && (
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={async () => {
                const current = downloadedPdfRef.current;
                if (!current) return;
                try {
                  // Must be called inside the tap gesture (iOS requirement).
                  // Share ONLY the file: adding title/text alongside files
                  // makes iOS share targets (notably Save to Files) save the
                  // text payload as a .txt instead of the actual PDF.
                  await navigator.share({ files: [current.file] });
                } catch (err) {
                  // User closed the share sheet — that's a normal outcome.
                }
                closeDownloadDialog();
              }}
            >
              Save / Share
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PedigreeView;
